import MetadataParser from '@/formats/MetadataParser';
import type Metadata from '../Metadata';
import type Audio from '../Audio';
import type Unit from '../units/Unit';
import type { Pixel } from '../units/UnitValue';
import { SourceType } from './SourceType';
import type { Image3DPageData } from './Image3DPageData';
import { ImageLikeSource } from './ImageLikeSource';
import type { MetadataFormat } from '../Metadata';

interface SourceData {
  pixelUnit: Unit<Pixel>;
  width: Pixel;
  height: Pixel;
  imageUrl: string;
  svgImage: string;
  type: SourceType;
  scale?: number;
  track?: Audio;
  metadata : Metadata;
  name?: string;
  pages?: Array<Image3DPageData>;
  source?: string;
}

/**
 * An image file object
 *
 * Contains:
 * * the relative path to an image file
 * * a time conversion object
 *
 * For score images, the conversion should be onset -> pixel
 *
 * For wave images, the conversion should be second -> pixel
 */
export interface FileFormat extends MetadataFormat{
  image: string;
  positions: string;
  type: SourceType;
  name?: string;
  source?: string;
  id?:string;
}

export interface ImageFileFormat extends FileFormat {
  type: SourceType.Score;
}

export interface Image3DFileFormat extends FileFormat {
  type: SourceType.Image3D;
}

export interface AudioImageFileFormat extends FileFormat {
  type: SourceType.AudioImage;
}

export interface ImageDimensions {
  height: Pixel,
  width: Pixel,
}

export default class Source extends ImageLikeSource implements Readonly<SourceData> {
  id = 0;

  // TODO: remove with the refactoring of the zoom
  scale: number | undefined = 1;

  imageUrl: string;

  svgImage: string;

  protected constructor(parsed: SourceData) {
    super({
      type: parsed.type,
      pixelUnit: parsed.pixelUnit,
      width: parsed.width,
      height: parsed.height,
      name: parsed.name,
      metadata: parsed.metadata,
      source: parsed.source,
      track: parsed.track,
      pages: parsed.pages,
      isLinear: (parsed.type === SourceType.Score || parsed.type === SourceType.AudioImage),
    });

    this.scale = parsed.scale;
    this.imageUrl = parsed.imageUrl;
    this.svgImage = parsed.svgImage;
  }

  setPage(index: number): void {
    if (!this.pages) return;
    const splittedImageUrl = this.imageUrl.split('/');
    splittedImageUrl.pop();
    this.imageUrl = `${splittedImageUrl.join('/')}/${this.pages[index].image}`;
  }

  scaled(scale: number): Source {
    const data = { ...this };
    return new Source({
      ...data,
      width: data.width * scale,
      scale,
      pixelUnit: data.pixelUnit.getScaled(scale),
    });
  }

  /**
   * Create a new source (ImageLikeSource)
   */
  static parse(
    format: FileFormat,
    dim: ImageDimensions,
    pixelUnit: Unit<Pixel>,
    imageUrl?: string,
    svgImage?:string,
    track?: Audio,
    pages?: Array<Image3DPageData>,
  ): Source {
    try {
      const type = String(format.type || '') as SourceType;

      const name = String(format.name || '');

      const source = format.source || undefined;

      const metadata : Metadata = MetadataParser.fromFormat(format);

      return new Source({
        pixelUnit,
        imageUrl: imageUrl || '',
        svgImage: svgImage || '',
        type,
        scale: undefined,
        name,
        track,
        pages,
        source,
        metadata,
        ...dim,
      });
    } catch (e) {
      const error = e as Error;
      throw new Error(`Corrupted Source: ${error.message}`);
    }
  }
}
